import { useNavigate } from "react-router-dom";
import { cartTotal, useStore } from "../store/useStore";
import { CardOnlyBanner } from "../components/CardOnlyBanner";
import { t } from "../lib/i18n";
import { LANGUAGES } from "../data/languages";
import { approxIn } from "../lib/fx";
import { BRAND_TAG } from "../lib/brand";

export default function OrderReview() {
  const lang = useStore((s) => s.language);
  const cart = useStore((s) => s.cart);
  const setQty = useStore((s) => s.setQty);
  const customerName = useStore((s) => s.customerName);
  const navigate = useNavigate();
  const total = cartTotal(cart);
  const totalQty = cart.reduce((s, c) => s + c.qty, 0);
  const isRtl = LANGUAGES.find((l) => l.code === lang)?.rtl;
  const itemsLabel = totalQty === 1 ? t(lang, "receipt_items_one") : t(lang, "receipt_items_many");

  if (cart.length === 0) {
    navigate("/kiosk/menu");
    return null;
  }

  return (
    <div
      className="grain min-h-screen w-full bg-paper px-6 py-8 flex flex-col items-center pb-32"
      dir={isRtl ? "rtl" : undefined}
    >
      <p className="font-mono text-xs tracking-[0.4em] text-mezcal uppercase">{t(lang, "review_caption")}</p>
      <h1 className="font-display font-black text-5xl text-center max-w-3xl tracking-kerned leading-[0.9] mt-2">
        {t(lang, "review_title")}
      </h1>
      <p className="font-hand text-2xl text-cactus mt-1">— {t(lang, "review_handwritten")}, {customerName.toLowerCase() || "—"} —</p>

      <div
        className="mt-8 w-full max-w-xl bg-paper border-4 border-ink p-6 relative shadow-[0_12px_32px_-8px_rgba(26,22,18,0.4)]"
        style={{ transform: "rotate(-0.6deg)" }}
      >
        <div className="absolute -top-3 left-6 bg-paper px-3 font-mono text-[10px] tracking-widest">{t(lang, "review_draft")}</div>
        <div className="absolute -top-3 right-6 bg-paper px-3 font-mono text-[10px] tracking-widest">№ {String(Date.now()).slice(-4)}</div>

        <div className="text-center font-display font-bold tracking-[0.3em] uppercase text-sm">{BRAND_TAG}</div>
        <div className="text-center font-mono text-[10px] tracking-widest text-ink/60 mt-1">
          {new Date().toLocaleDateString(lang || "es", { day: "2-digit", month: "short", year: "numeric" })} · {new Date().toLocaleTimeString(lang || "es", { hour: "2-digit", minute: "2-digit" })}
        </div>

        <div className="my-3 border-t border-dashed border-ink/40" />

        <div className="flex justify-between font-mono text-xs tracking-widest text-ink/60">
          <span>{t(lang, "review_customer")}</span>
          <span className="font-display font-bold text-ink text-base tracking-normal">{customerName || "—"}</span>
        </div>

        <div className="my-3 border-t border-dashed border-ink/40" />

        <div className="font-mono text-base">
          {cart.map((it) => (
            <div key={it.sku} className="py-2 border-b border-dotted border-ink/20 last:border-0">
              <div className="flex justify-between items-baseline">
                <span className="font-display font-bold text-lg pr-2 truncate">{it.product.name}</span>
                <span className="tabular-nums font-bold">${it.qty * it.product.price}</span>
              </div>
              <div className="flex justify-between items-center mt-1">
                <span className="text-xs text-ink/60">${it.product.price} {t(lang, "review_per_unit")}</span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setQty(it.sku, it.qty - 1)}
                    className="w-8 h-8 rounded-full bg-paper border-2 border-ink active:bg-ink active:text-paper font-bold"
                  >−</button>
                  <span className="w-6 text-center font-display font-bold text-lg tabular-nums">{it.qty}</span>
                  <button
                    onClick={() => setQty(it.sku, it.qty + 1)}
                    className="w-8 h-8 rounded-full bg-paper border-2 border-ink active:bg-ink active:text-paper font-bold"
                  >+</button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="my-3 border-t border-dashed border-ink/40" />

        <div className="flex justify-between font-mono text-sm">
          <span>{totalQty} {itemsLabel}</span>
          <span className="tabular-nums">${total}</span>
        </div>
        <div className="flex justify-between items-baseline mt-2">
          <span className="font-display font-black text-3xl tracking-kerned">{t(lang, "review_total")}</span>
          <div className="text-right">
            <div className="font-display font-black text-4xl tabular-nums">${total}</div>
            {approxIn(lang, total) && (
              <div className="font-mono text-xs text-ink/50 mt-0.5">{approxIn(lang, total)}</div>
            )}
          </div>
        </div>

        <div className="mt-3 text-center font-mono text-[10px] tracking-widest text-ink/50">
          {t(lang, "review_tip_note")}
        </div>
      </div>

      <div className="mt-6">
        <CardOnlyBanner variant="dark" size="md" />
      </div>

      {/* Floating action bar — sticks to bottom over scrolling content */}
      <div className="fixed bottom-0 inset-x-0 z-30 bg-paper/95 backdrop-blur-md border-t-2 border-ink/15 shadow-[0_-12px_24px_-8px_rgba(26,22,18,0.25)]">
        <div className="max-w-xl mx-auto px-6 py-4 flex gap-3">
          <button
            onClick={() => navigate("/kiosk/menu")}
            className="flex-1 py-4 rounded-sm border-2 border-ink/30 font-display font-bold tracking-wide text-lg active:bg-ink active:text-paper"
          >
            ← {t(lang, "review_edit")}
          </button>
          <button
            onClick={() => navigate("/kiosk/pay")}
            className="flex-[2] py-4 rounded-sm bg-mezcal text-paper font-display font-bold tracking-wide text-lg shadow-[0_8px_24px_-8px_rgba(200,75,26,0.7)] active:scale-[0.98]"
          >
            {t(lang, "review_confirm")} · ${total} →
          </button>
        </div>
      </div>
    </div>
  );
}
